import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaClipboardList,
  FaUserCheck,
  FaPlay,
  FaChartBar,
  FaArrowsAltV,
  FaPhone,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaShieldAlt,
  FaRocket,
  FaCalculator,
  FaHandshake,
  FaFileAlt,
  FaComments,
  FaCodeBranch,
  FaSync
} from 'react-icons/fa';

const HowItWorks = () => {
  const steps = [
    {
      number: 1,
      title: 'Share Your Requirements',
      description: 'Tell us roles, tech stack, duration, and goals.',
      icon: <FaClipboardList />,
      details: [
        'Define engineering roles needed (Frontend, Backend, Full Stack, Mobile, QA, DevOps)',
        'Specify technology stack and framework preferences',
        'Share project timeline and expected duration',
        'Communicate team size requirements and timezone preferences',
        'Discuss business goals and success metrics'
      ],
      timeline: '1-2 days',
      deliverables: 'Requirements document, project scope, team structure'
    },
    {
      number: 2,
      title: 'Review Developer Profiles',
      description: 'We share vetted profiles within 24--72 hours.',
      icon: <FaUserCheck />,
      details: [
        'Receive pre-vetted candidate profiles matching your requirements',
        'Review technical skills, experience, and portfolio',
        'Conduct technical interviews with shortlisted candidates',
        'Verify communication skills and cultural fit',
        'Check references and background verification'
      ],
      timeline: '24-72 hours',
      deliverables: 'Candidate profiles, technical assessments, interview scheduling'
    },
    {
      number: 3,
      title: 'Onboard & Start',
      description: 'Developers join your tools, standups, and sprints.',
      icon: <FaPlay />,
      details: [
        'Complete NDA and IP protection agreements',
        'Set up access to your tools (Jira, Slack, GitHub, etc.)',
        'Join daily standups and sprint planning meetings',
        'Review codebase and project documentation',
        'Begin contributing to your development workflow'
      ],
      timeline: '1-3 days',
      deliverables: 'Onboarded developers, access setup, integration complete'
    },
    {
      number: 4,
      title: 'Ongoing Delivery',
      description: 'Weekly updates, clear ownership, transparency.',
      icon: <FaChartBar />,
      details: [
        'Weekly progress reports and sprint reviews',
        'Clear task ownership and accountability',
        'Transparent communication via your preferred channels',
        'Regular code reviews and quality checks',
        'Continuous feedback and performance monitoring'
      ],
      timeline: 'Ongoing',
      deliverables: 'Weekly reports, code delivery, sprint completion'
    },
    {
      number: 5,
      title: 'Scale Anytime',
      description: 'Add or reduce resources without long-term commitments.',
      icon: <FaArrowsAltV />,
      details: [
        'Add more developers as project scope expands',
        'Reduce team size when requirements change',
        'No minimum contract periods or penalties',
        'Fast replacement if a resource isn\'t the right fit',
        'Flexible scaling based on project needs'
      ],
      timeline: 'As needed',
      deliverables: 'Scaled team, adjusted resources, optimized capacity'
    }
  ];

  const processBenefits = [
    {
      icon: <FaClock />,
      title: 'Fast Turnaround',
      description: 'Get developers ready in 24-72 hours, not weeks or months.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Risk-Free Trial',
      description: 'Fast replacement guarantee if expectations aren\'t met.'
    },
    {
      icon: <FaUsers />,
      title: 'Seamless Integration',
      description: 'Developers work as part of your team using your tools and processes.'
    },
    {
      icon: <FaHandshake />,
      title: 'No Long-Term Commitments',
      description: 'Scale up or down anytime without penalties or minimum periods.'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section" style={{ paddingTop: '120px', backgroundColor: 'var(--bg-grey-light)', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
          zIndex: 1
        }}></div>
        <div className="container text-center" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="h1" style={{ marginBottom: '24px' }}>
            How Our Staff Augmentation Model Works
          </h1>
          <p className="text-lg mb-6" style={{ maxWidth: '700px', margin: '0 auto 32px', fontSize: '20px', color: 'var(--text-medium)' }}>
            Clear and transparent engagement model designed for fast scaling and seamless integration. 
            From requirement sharing to team scaling, we make the process simple and efficient.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/calculator" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalculator /> Get Team Cost
            </Link>
            <Link to="/contact" className="btn btn-secondary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPhone /> Schedule a Call
            </Link>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Our 5-Step Process</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              A streamlined process that gets your team up and running quickly
            </p>
          </div>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            {steps.map((step, index) => (
              <div key={index} style={{
                marginBottom: '48px',
                padding: '40px',
                backgroundColor: 'white',
                borderRadius: 'var(--border-radius-lg)',
                boxShadow: 'var(--shadow-sm)',
                border: '1px solid var(--border-grey)',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary-green)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    boxShadow: 'var(--shadow-md)'
                  }}>
                    {step.number}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                      <div style={{ fontSize: '32px', color: 'var(--primary-green)' }}>
                        {step.icon}
                      </div>
                      <h3 className="h3 text-green" style={{ margin: 0 }}>{step.title}</h3>
                    </div>
                    <p style={{ fontSize: '18px', color: 'var(--text-medium)', marginBottom: '24px', lineHeight: '1.6' }}>
                      {step.description}
                    </p>
                    
                    <div style={{
                      backgroundColor: 'var(--bg-grey-light)',
                      padding: '20px',
                      borderRadius: 'var(--border-radius)',
                      marginBottom: '20px'
                    }}>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--primary-green)', marginBottom: '12px' }}>
                        What Happens:
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {step.details.map((detail, idx) => (
                          <li key={idx} style={{
                            fontSize: '14px',
                            color: 'var(--text-dark)',
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            lineHeight: '1.5'
                          }}>
                            <FaCheckCircle style={{ color: 'var(--primary-green)', fontSize: '16px', marginTop: '2px', flexShrink: 0 }} />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      <div style={{
                        backgroundColor: 'white',
                        border: '1px solid var(--border-grey)',
                        padding: '12px 20px',
                        borderRadius: 'var(--border-radius)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaClock style={{ color: 'var(--primary-green)', fontSize: '18px' }} />
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-medium)', fontWeight: '600' }}>Timeline</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-dark)', fontWeight: '600' }}>{step.timeline}</div>
                        </div>
                      </div>
                      <div style={{
                        backgroundColor: 'white',
                        border: '1px solid var(--border-grey)',
                        padding: '12px 20px',
                        borderRadius: 'var(--border-radius)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaFileAlt style={{ color: 'var(--primary-green)', fontSize: '18px' }} />
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-medium)', fontWeight: '600' }}>Deliverables</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-dark)', fontWeight: '600' }}>{step.deliverables}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: '-24px',
                    transform: 'translateX(-50%)',
                    width: '2px',
                    height: '48px',
                    backgroundColor: 'var(--primary-green)',
                    opacity: 0.3
                  }}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Benefits */}
      <section className="section section-grey">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Why Our Process Works</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              Our streamlined approach ensures you get the right talent, fast, with minimal friction.
            </p>
          </div>
          <div className="grid grid-2">
            {processBenefits.map((benefit, index) => (
              <div key={index} className="card" style={{ padding: '32px', display: 'flex', gap: '24px' }}>
                <div style={{ fontSize: '40px', color: 'var(--primary-green)', flexShrink: 0 }}>
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="h4 mb-3" style={{ color: 'var(--text-dark)' }}>{benefit.title}</h3>
                  <p style={{ color: 'var(--text-medium)', lineHeight: '1.6' }}>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">What to Expect</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              Clear expectations and deliverables at every stage of the process
            </p>
          </div>
          <div className="grid grid-2" style={{ gap: '32px' }}>
            <div className="card" style={{ padding: '32px' }}>
              <FaComments style={{ fontSize: '40px', color: 'var(--primary-green)', marginBottom: '20px' }} />
              <h3 className="h4 mb-4">Communication</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Weekly progress reports and sprint reviews</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Direct access to developers via your communication channels</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Dedicated account manager for support and coordination</span>
                </li>
              </ul>
            </div>
            <div className="card" style={{ padding: '32px' }}>
              <FaCodeBranch style={{ fontSize: '40px', color: 'var(--primary-green)', marginBottom: '20px' }} />
              <h3 className="h4 mb-4">Integration</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Work inside your Jira, Slack, GitHub, and other tools</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Participate in daily standups and sprint planning</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Follow your coding standards and best practices</span>
                </li>
              </ul>
            </div>
            <div className="card" style={{ padding: '32px' }}>
              <FaSync style={{ fontSize: '40px', color: 'var(--primary-green)', marginBottom: '20px' }} />
              <h3 className="h4 mb-4">Flexibility</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Scale team size up or down as needed</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>No minimum contract periods or penalties</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Fast replacement if a resource isn't the right fit</span>
                </li>
              </ul>
            </div>
            <div className="card" style={{ padding: '32px' }}>
              <FaShieldAlt style={{ fontSize: '40px', color: 'var(--primary-green)', marginBottom: '20px' }} />
              <h3 className="h4 mb-4">Security & Support</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>NDA and IP protection agreements in place</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Secure access controls and least-privilege policies</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaCheckCircle style={{ color: 'var(--primary-green)', marginTop: '4px', flexShrink: 0 }} />
                  <span>Ongoing support and performance monitoring</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-grey">
        <div className="container text-center">
          <FaRocket style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '16px' }} />
          <h2 className="h2 mb-6">Start building your engineering team today</h2>
          <p className="text-lg mb-8" style={{ maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
            Get started with our staff augmentation process and scale your team without hiring delays. 
            Share your requirements and we'll have developers ready in 24-72 hours.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPhone /> Schedule a Call
            </Link>
            <Link to="/calculator" className="btn btn-secondary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalculator /> Get Team Cost
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
